import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useMemo } from 'react'
import {
    Body,
    ButtonAction,
    ButtonsContainer,
    DataBox,
    NotificationDoneBy,
    dialogPanel,
    dialogTitle
} from './styles'
import { IoCloseSharp } from 'react-icons/io5'
import NotificationService from 'services/Notification'
import { useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { AuthenticationReducer } from 'types/autentication'
import { notificationStatus } from 'constants/notificationStatus'
import { getCorIncidente, getDamage } from 'utils/damage'
import { getRoleName } from 'utils/getRole'
import OutlineCheckIcon from 'components/Icons/OutlineCheckIcon'
import { OutlinePersonIcon } from 'components/Icons/OutlinePersonIcon'

interface FinishNotificationModalProps {
    handleCloseModal: () => void
    isOpen: boolean
    submitFinished: () => void
    notificationById: any
    finishErrorMessage: string
    finishModalData: {
        submitButtonText: string;
        title: string;
        subtitle: string;
        type: 'forward' | 'finish';
    }
}

const FinishNotificationModal: React.FC<FinishNotificationModalProps> = ({
    handleCloseModal,
    isOpen,
    submitFinished,
    notificationById,
    finishErrorMessage,
    finishModalData
}) => {
    const authenticationSelector: AuthenticationReducer = useSelector(
        (state: RootReducerState) => state.authenticationReducers
    )
    const profileStatus = useMemo(() => {
        if (authenticationSelector?.dataJwt?.profile) {
            return getRoleName(authenticationSelector?.dataJwt?.profile)
        }
    }, [authenticationSelector])

    const submitForum = async () => {
        submitFinished()
    }

    return (
        <>
            <Transition
                appear
                show={isOpen}
                as={Fragment}
            >
                <Dialog
                    as="div"
                    className="relative z-10"
                    onClose={handleCloseModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className={`${dialogPanel}`}>
                                    <Dialog.Title
                                        as="h3"
                                        className={dialogTitle}
                                    >
                                        <p className="text-blueTheme text-xl font-semibold">
                                            {finishModalData.title}
                                        </p>
                                        <IoCloseSharp
                                            onClick={handleCloseModal}
                                            className="text-blueTheme h-8 w-8 p-1 cursor-pointer"
                                        />
                                    </Dialog.Title>
                                    <Body >
                                        <p className="text-sm">
                                            {finishModalData.subtitle}
                                        </p>
                                        <DataBox>
                                            <div className="flex gap-1 items-center mb-2">
                                                <OutlineCheckIcon
                                                    width="17"
                                                    height="height"
                                                    color="#5FD290"
                                                />
                                                <span className="text-[13px] text-darkGray font-semibold">
                                                    {
                                                        notificationById
                                                            ?.incident_title
                                                            ?.display_value
                                                    }
                                                </span>
                                            </div>
                                            <div className="flex gap-4 items-center mb-2 ml-[22px]">
                                                <span className="text-base text-darkGray font-bold uppercase">
                                                    {
                                                        notificationById?.number
                                                            ?.display_value
                                                    }
                                                </span>
                                                <span className="text-xs text-darkGray">
                                                    {
                                                        notificationById
                                                            ?.date_incident
                                                            ?.display_value
                                                    }
                                                </span>
                                            </div>
                                            <div className="flex gap-4 items-center mb-2 ml-[22px]">
                                                <span className="text-xs text-darkGray">
                                                    {
                                                        notificationById
                                                            ?.location_incident
                                                            ?.display_value
                                                    }
                                                </span>
                                            </div>
                                            <div className="flex gap-4 items-center mb-2 ml-[22px]">
                                                <span
                                                    className={`chip-incidente incidente-${getCorIncidente(
                                                        getDamage(
                                                            notificationById
                                                                ?.damage_level
                                                                ?.value
                                                        )?.value
                                                    )}`}
                                                >
                                                    {`${getDamage(
                                                        notificationById
                                                            ?.damage_level
                                                            ?.value
                                                    )?.label
                                                        }`}
                                                </span>
                                            </div>
                                        </DataBox>
                                        {
                                            finishModalData.type === 'finish' ?
                                                <NotificationDoneBy>
                                                    <span className="text-xs text-darkGray">
                                                        Essa notificação será finalizada
                                                        por
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <OutlinePersonIcon
                                                            width="24"
                                                            height="24"
                                                            color="#893367"
                                                        />
                                                        <span className="text-base text-roxo font-semibold">
                                                            {
                                                                authenticationSelector
                                                                    ?.dataJwt?.name
                                                            }
                                                        </span>
                                                    </div>
                                                    <span className="text-sm text-darkGray">
                                                        {profileStatus?.title}
                                                    </span>
                                                </NotificationDoneBy> : null
                                        }
                                        <ButtonsContainer>
                                            <ButtonAction
                                                onClick={() => {
                                                    handleCloseModal()
                                                }}
                                                type="button"
                                                className="text-blueTheme text-sm"
                                            >
                                                Cancelar
                                            </ButtonAction>

                                            <ButtonAction
                                                onClick={submitForum}
                                                className="bg-blueTheme text-white text-sm"
                                            >
                                                {finishModalData.submitButtonText}
                                            </ButtonAction>
                                        </ButtonsContainer>
                                        {finishErrorMessage && (
                                            <span className="text-errorText flex justify-center mt-2 text-sm">
                                                {finishErrorMessage}
                                            </span>
                                        )}
                                    </Body>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

export { FinishNotificationModal }
